import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS - Enhanced with better color harmony
const PRIMARY = {
  lighter: '#E8F5E8',
  light: '#66BB6A',
  main: '#4CAF50',
  dark: '#388E3C',
  darker: '#1B5E20',
  50: '#F1F8E9',
  100: '#DCEDC8',
  200: '#C5E1A5',
  300: '#AED581',
  400: '#9CCC65',
  500: '#8BC34A',
  600: '#7CB342',
  700: '#689F38',
  800: '#558B2F',
  900: '#33691E',
  contrastText: '#fff',
};

const SECONDARY = {
  lighter: '#E3F2FD',
  light: '#42A5F5',
  main: '#2196F3',
  dark: '#1976D2',
  darker: '#0D47A1',
  50: '#E3F2FD',
  100: '#BBDEFB',
  200: '#90CAF9',
  300: '#64B5F6',
  400: '#42A5F5',
  500: '#2196F3',
  600: '#1E88E5',
  700: '#1976D2',
  800: '#1565C0',
  900: '#0D47A1',
  contrastText: '#fff',
};

const INFO = {
  lighter: '#E0F7FA',
  light: '#4DD0E1',
  main: '#00BCD4',
  dark: '#0097A7',
  darker: '#006064',
  contrastText: '#fff',
};

const SUCCESS = {
  lighter: '#E8F5E8',
  light: '#81C784',
  main: '#4CAF50',
  dark: '#388E3C',
  darker: '#1B5E20',
  contrastText: '#fff',
};

const WARNING = {
  lighter: '#FFF8E1',
  light: '#FFD54F',
  main: '#FFC107',
  dark: '#F57C00',
  darker: '#E65100',
  contrastText: '#212B36',
};

const ERROR = {
  lighter: '#FFEBEE',
  light: '#EF5350',
  main: '#F44336',
  dark: '#D32F2F',
  darker: '#B71C1C',
  contrastText: '#fff',
};

const PURPLE = {
  lighter: '#F3E5F5',
  light: '#BA68C8',
  main: '#9C27B0',
  dark: '#7B1FA2',
  darker: '#4A148C',
  contrastText: '#fff',
};

const ORANGE = {
  lighter: '#FFF3E0',
  light: '#FFB74D',
  main: '#FF9800',
  dark: '#F57C00',
  darker: '#E65100',
  contrastText: '#fff',
};

const TEAL = {
  lighter: '#E0F2F1',
  light: '#4DB6AC',
  main: '#009688',
  dark: '#00695C',
  darker: '#004D40',
  contrastText: '#fff',
};

const GREY = {
  0: '#FFFFFF',
  50: '#FAFAFA',
  100: '#F5F5F5',
  200: '#EEEEEE',
  300: '#E0E0E0',
  400: '#BDBDBD',
  500: '#9E9E9E',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121',
  A100: '#F5F5F5',
  A200: '#EEEEEE',
  A400: '#BDBDBD',
  A700: '#616161',
  // Custom alpha variants
  500_8: alpha('#9E9E9E', 0.08),
  500_12: alpha('#9E9E9E', 0.12),
  500_16: alpha('#9E9E9E', 0.16),
  500_24: alpha('#9E9E9E', 0.24),
  500_32: alpha('#9E9E9E', 0.32),
  500_48: alpha('#9E9E9E', 0.48),
  500_56: alpha('#9E9E9E', 0.56),
  500_80: alpha('#9E9E9E', 0.8),
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  secondary: createGradient(SECONDARY.light, SECONDARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
  purple: createGradient(PURPLE.light, PURPLE.main),
  orange: createGradient(ORANGE.light, ORANGE.main),
  teal: createGradient(TEAL.light, TEAL.main),
  // 特殊渐变 - 增强版
  glass: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
  glassDark: 'linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.05) 100%)',
  rainbow: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  sunset: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  ocean: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  forest: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',
  fire: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  sky: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  night: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
  aurora: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
  cosmic: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  warm: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
  cool: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  // 卡片专用渐变
  cardPrimary: `linear-gradient(135deg, ${alpha(PRIMARY.main, 0.1)} 0%, ${alpha(PRIMARY.main, 0.05)} 100%)`,
  cardSecondary: `linear-gradient(135deg, ${alpha(SECONDARY.main, 0.1)} 0%, ${alpha(SECONDARY.main, 0.05)} 100%)`,
  cardSuccess: `linear-gradient(135deg, ${alpha(SUCCESS.main, 0.1)} 0%, ${alpha(SUCCESS.main, 0.05)} 100%)`,
  cardWarning: `linear-gradient(135deg, ${alpha(WARNING.main, 0.1)} 0%, ${alpha(WARNING.main, 0.05)} 100%)`,
  cardError: `linear-gradient(135deg, ${alpha(ERROR.main, 0.1)} 0%, ${alpha(ERROR.main, 0.05)} 100%)`,
};

const CHART_COLORS = {
  violet: ['#9C27B0', '#BA68C8', '#CE93D8', '#E1BEE7'],
  blue: ['#2196F3', '#64B5F6', '#90CAF9', '#BBDEFB'],
  green: ['#4CAF50', '#81C784', '#A5D6A7', '#C8E6C9'],
  yellow: ['#FFC107', '#FFD54F', '#FFE082', '#FFECB3'],
  red: ['#F44336', '#EF5350', '#E57373', '#EF9A9A'],
  orange: ['#FF9800', '#FFB74D', '#FFCC02', '#FFE0B2'],
  teal: ['#009688', '#4DB6AC', '#80CBC4', '#B2DFDB'],
  purple: ['#9C27B0', '#AB47BC', '#BA68C8', '#CE93D8'],
  indigo: ['#3F51B5', '#5C6BC0', '#7986CB', '#9FA8DA'],
  pink: ['#E91E63', '#EC407A', '#F06292', '#F48FB1'],
};

const COMMON = {
  common: { black: '#191919', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: { 
      primary: GREY[800], 
      secondary: GREY[600], 
      disabled: GREY[500],
      hint: GREY[500]
    },
    background: { 
      paper: '#fff', 
      default: GREY[50], 
      neutral: GREY[100],
      level1: GREY[100],
      level2: GREY[200],
      level3: GREY[300],
      surface: '#fff',
      surfaceVariant: GREY[50],
      outline: GREY[300],
      outlineVariant: GREY[200],
      // 特殊背景
      glass: 'rgba(255, 255, 255, 0.8)',
      blur: 'rgba(255, 255, 255, 0.9)',
      gradient: GRADIENTS.glass
    },
    action: {
      active: GREY[600],
      hover: alpha(GREY[500], 0.08),
      selected: alpha(GREY[500], 0.12),
      disabled: alpha(GREY[500], 0.26),
      disabledBackground: alpha(GREY[500], 0.12),
      focus: alpha(GREY[500], 0.12),
      hoverOpacity: 0.08,
      disabledOpacity: 0.38,
    },
    divider: alpha(GREY[500], 0.12),
    // 新增颜色
    purple: PURPLE,
    orange: ORANGE,
    teal: TEAL,
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: { 
      primary: '#fff', 
      secondary: GREY[400], 
      disabled: GREY[600],
      hint: GREY[500]
    },
    background: { 
      paper: GREY[800], 
      default: GREY[900], 
      neutral: alpha(GREY[500], 0.16),
      level1: GREY[800],
      level2: GREY[700],
      level3: GREY[600],
      surface: GREY[800],
      surfaceVariant: GREY[700],
      outline: GREY[600],
      outlineVariant: GREY[700],
      // 特殊背景
      glass: 'rgba(0, 0, 0, 0.8)',
      blur: 'rgba(0, 0, 0, 0.9)',
      gradient: GRADIENTS.glassDark
    },
    action: {
      active: GREY[400],
      hover: alpha(GREY[500], 0.08),
      selected: alpha(GREY[500], 0.16),
      disabled: alpha(GREY[500], 0.26),
      disabledBackground: alpha(GREY[500], 0.12),
      focus: alpha(GREY[500], 0.12),
      hoverOpacity: 0.08,
      disabledOpacity: 0.38,
    },
    divider: alpha(GREY[500], 0.12),
    // 新增颜色
    purple: PURPLE,
    orange: ORANGE,
    teal: TEAL,
  },
};

export default palette;
